import { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import { ArrowLeft, Clock, Calendar, MapPin } from "lucide-react"
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api"
import FavoriteButton from "../components/FavoriteButton"
import TempleService from "../services/templeService"
import Navbar from "../components/Navbar"


export default function TempleDetailsPage() {
  const { id } = useParams()
  const [temple, setTemple] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [activeTab, setActiveTab] = useState("overview")

  useEffect(() => {
    const fetchTempleData = async () => {
      try {
        setLoading(true)
        const data = await TempleService.getTempleById(id)
        setTemple(data)
      } catch (err) {
        console.error("Error fetching temple data:", err)
        setError("Failed to load temple information. Please try again later.")
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchTempleData()
    }
  }, [id])

  // Auto-rotate images if available
  useEffect(() => {
    if (temple && temple.images && temple.images.length > 0) {
      const interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % temple.images.length)
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [temple])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-divine-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-spiritual-600 mb-4"></div>
          <p className="text-gray-600">Loading temple information...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-divine-50">
        <div className="text-center max-w-md px-4">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Something went wrong</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link
            to="/temple"
            className="bg-spiritual-600 hover:bg-spiritual-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Back to Temples
          </Link>
        </div>
      </div>
    )
  }

  if (!temple) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-divine-50">
        <div className="text-center max-w-md px-4">
          <div className="text-gray-400 text-5xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Temple Not Found</h1>
          <p className="text-gray-600 mb-6">The temple you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/temple"
            className="bg-spiritual-600 hover:bg-spiritual-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Browse Temples
          </Link>
        </div>
      </div>
    )
  }

  // Prepare images array for carousel
  const images =
    temple.images && temple.images.length > 0
      ? temple.images.map((img) => (typeof img === "string" ? img : img.path || ""))
      : [temple.mainImage || "/placeholder.svg?height=600&width=800"]

  // Ensure we have valid coordinates for the map
  const coordinates =
    temple.location && temple.location.coordinates ? temple.location.coordinates : { lat: 20.5937, lng: 78.9629 } // Default to center of India if no coordinates

  return (
    <div className="min-h-screen bg-gradient-to-b from-divine-50 via-white to-sacred-50">
      {/* Navigation */}
      <Navbar />

      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Link
          to="/temple"
          className="inline-flex items-center text-spiritual-600 hover:text-spiritual-800 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Temples
        </Link>
      </div>

      {/* Temple Header */}
      <header className="relative">
        {/* Image Carousel */}
        <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
          {images.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentImageIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${image})` }}>
                <div className="absolute inset-0 bg-black bg-opacity-30"></div>
              </div>
            </div>
          ))}

          {/* Temple Name Overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4">
            <div className="flex items-center mb-2">
              <h1 className="text-4xl md:text-6xl font-bold drop-shadow-lg">{temple.name}</h1>
              <div className="ml-4">
                <FavoriteButton itemId={temple._id} itemType="temple" className="bg-white/20 hover:bg-white/30" />
              </div>
            </div>
            <p className="text-xl md:text-2xl drop-shadow-md">
              {temple.deity} • {temple.location && temple.location.state}
            </p>
          </div>

          {/* Carousel Indicators */}
          {images.length > 1 && (
            <div className="absolute bottom-6 left-0 right-0 flex justify-center space-x-2">
              {images.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`w-3 h-3 rounded-full ${
                    index === currentImageIndex ? "bg-white" : "bg-white bg-opacity-50"
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-16 mb-8 relative z-10">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-3">
              <Clock className="h-5 w-5 text-spiritual-600 mr-2" />
              <h3 className="text-lg font-bold text-spiritual-700">Opening Hours</h3>
            </div>
            <p className="text-gray-700">{temple.timings || "Contact temple for current hours"}</p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-3">
              <MapPin className="h-5 w-5 text-spiritual-600 mr-2" />
              <h3 className="text-lg font-bold text-spiritual-700">Location</h3>
            </div>
            <p className="text-gray-700">
              {temple.location
                ? `${temple.location.address}, ${temple.location.city}, ${temple.location.state}`
                : "Location information not available"}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center mb-3">
              <Calendar className="h-5 w-5 text-spiritual-600 mr-2" />
              <h3 className="text-lg font-bold text-spiritual-700">Category</h3>
            </div>
            <p className="text-gray-700">{temple.category || "General"}</p>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="flex overflow-x-auto hide-scrollbar">
            <button
              onClick={() => setActiveTab("overview")}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap ${
                activeTab === "overview"
                  ? "border-b-2 border-spiritual-600 text-spiritual-700"
                  : "text-gray-500 hover:text-spiritual-600"
              }`}
            >
              Overview
            </button>
            {temple.history && (
              <button
                onClick={() => setActiveTab("history")}
                className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap ${
                  activeTab === "history"
                    ? "border-b-2 border-spiritual-600 text-spiritual-700"
                    : "text-gray-500 hover:text-spiritual-600"
                }`}
              >
                History
              </button>
            )}
            <button
              onClick={() => setActiveTab("location")}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap ${
                activeTab === "location"
                  ? "border-b-2 border-spiritual-600 text-spiritual-700"
                  : "text-gray-500 hover:text-spiritual-600"
              }`}
            >
              Location
            </button>
            {temple.festivals && temple.festivals.length > 0 && (
              <button
                onClick={() => setActiveTab("festivals")}
                className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap ${
                  activeTab === "festivals"
                    ? "border-b-2 border-spiritual-600 text-spiritual-700"
                    : "text-gray-500 hover:text-spiritual-600"
                }`}
              >
                Festivals
              </button>
            )}
            {temple.nearbyAttractions && temple.nearbyAttractions.length > 0 && (
              <button
                onClick={() => setActiveTab("nearby")}
                className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap ${
                  activeTab === "nearby"
                    ? "border-b-2 border-spiritual-600 text-spiritual-700"
                    : "text-gray-500 hover:text-spiritual-600"
                }`}
              >
                Nearby Attractions
              </button>
            )}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mb-12">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-spiritual-700 mb-4">About {temple.name}</h2>
                <p className="text-gray-700 mb-6 leading-relaxed">{temple.description}</p>

                {temple.architecture && (
                  <>
                    <h3 className="text-xl font-bold text-spiritual-700 mb-3">Architecture</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">{temple.architecture}</p>
                  </>
                )}

                {temple.importance && (
                  <>
                    <h3 className="text-xl font-bold text-spiritual-700 mb-3">Religious Importance</h3>
                    <p className="text-gray-700 mb-6 leading-relaxed">{temple.importance}</p>
                  </>
                )}

                {temple.visitingTips && (
                  <>
                    <h3 className="text-xl font-bold text-spiritual-700 mb-3">Visiting Tips</h3>
                    <p className="text-gray-700 leading-relaxed">{temple.visitingTips}</p>
                  </> 
                )}
              </div>

              <div>
                <div className="bg-sacred-50 rounded-lg p-6 mb-6">
                  <h3 className="text-xl font-bold text-sacred-700 mb-4">Opening Hours</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium">{temple.timings || "Contact temple for current hours"}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-royal-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-royal-700 mb-4">Quick Info</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-royal-500 rounded-full mt-2 mr-2"></span>
                      <span>
                        <strong>Deity:</strong> {temple.deity}
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-royal-500 rounded-full mt-2 mr-2"></span>
                      <span>
                        <strong>Category:</strong> {temple.category}
                      </span>
                    </li>
                    <li className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-royal-500 rounded-full mt-2 mr-2"></span>
                      <span>
                        <strong>State:</strong> {temple.location && temple.location.state}
                      </span>
                    </li>
                    {temple.featured && (
                      <li className="flex items-start">
                        <span className="inline-block w-2 h-2 bg-royal-500 rounded-full mt-2 mr-2"></span>
                        <span>
                          <strong>Featured Temple</strong>
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div>
              <h2 className="text-2xl font-bold text-spiritual-700 mb-4">History of {temple.name}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <p className="text-gray-700 mb-6 leading-relaxed">
                    {temple.history || "Historical information about this temple is not available at the moment."}
                  </p>
                </div>
                <div>
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <img
                      src={temple.mainImage || "/placeholder.svg?height=300&width=400"}
                      alt={temple.name}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-4">
                      <h3 className="font-bold text-spiritual-700 mb-2">Historical Significance</h3>
                      <p className="text-gray-700 text-sm">
                        {temple.history
                          ? "This temple holds significant historical and cultural importance in the region."
                          : "Historical information is being compiled."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === "location" && (
            <div>
              <h2 className="text-2xl font-bold text-spiritual-700 mb-6">Location of {temple.name}</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <div className="h-96 w-full rounded-lg overflow-hidden mb-6">
                    <LoadScript googleMapsApiKey="AIzaSyDVp22TSezfAcdYUa8hZOZlCcHxCLZNu9A">
                      <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={coordinates} zoom={15}>
                        <Marker position={coordinates} />
                      </GoogleMap>
                    </LoadScript>
                  </div>
                </div>

                <div>
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-xl font-bold text-spiritual-700 mb-4">Address</h3>
                    <p className="text-gray-700 mb-4">
                      {temple.location
                        ? `${temple.location.address}, ${temple.location.city}, ${temple.location.state}, ${temple.location.country}`
                        : "Complete address information not available"}
                    </p>

                    <h3 className="text-xl font-bold text-spiritual-700 mb-4 mt-6">Coordinates</h3>
                    <p className="text-gray-700">Latitude: {coordinates.lat.toFixed(6)}</p>
                    <p className="text-gray-700">Longitude: {coordinates.lng.toFixed(6)}</p>

                    <div className="mt-6">
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-spiritual-600 hover:bg-spiritual-700 text-white px-4 py-2 rounded-md transition-colors"
                      >
                        <MapPin className="h-4 w-4 mr-2" />
                        Get Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Festivals Tab */}
          {activeTab === "festivals" && (
            <div>
              <h2 className="text-2xl font-bold text-spiritual-700 mb-6">Festivals at {temple.name}</h2>

              {temple.festivals && temple.festivals.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {temple.festivals.map((festival, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                      <div className="bg-spiritual-600 text-white py-3 px-4">
                        <h3 className="text-lg font-bold">{festival.name}</h3>
                      </div>
                      <div className="p-4">
                        <p className="text-gray-600 mb-2">
                          <Calendar className="h-4 w-4 inline mr-2" />
                          {festival.date || "Annual celebration"}
                        </p>
                        <p className="text-gray-700">
                          {festival.description || "A significant festival celebrated at this temple."}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="text-6xl text-gray-300 mb-4">🪔</div>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">No Festival Information Available</h3>
                  <p className="text-gray-600">
                    Information about festivals celebrated at this temple is currently being compiled.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Nearby Attractions Tab */}
          {activeTab === "nearby" && (
            <div>
              <h2 className="text-2xl font-bold text-spiritual-700 mb-6">Nearby Attractions</h2>

              {temple.nearbyAttractions && temple.nearbyAttractions.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {temple.nearbyAttractions.map((attraction, index) => (
                    <div key={index} className="bg-white rounded-lg shadow-lg p-6">
                      <div className="flex justify-between items-start">
                        <h3 className="text-xl font-bold text-spiritual-700 mb-2">{attraction.name}</h3>
                        <span className="bg-spiritual-100 text-spiritual-800 text-xs px-2 py-1 rounded-full">
                          {attraction.distance}
                        </span>
                      </div>
                      <p className="text-gray-700">
                        {attraction.description || "A popular attraction near this temple."}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <div className="text-6xl text-gray-300 mb-4">🗺️</div>
                  <h3 className="text-xl font-bold text-gray-700 mb-2">No Nearby Attractions Listed</h3>
                  <p className="text-gray-600">
                    Information about attractions near this temple is currently being compiled.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-royal-700 to-royal-900 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Plan Your Visit to {temple.name}</h2>
          <p className="text-xl max-w-3xl mx-auto mb-8">
            Ready to embark on a spiritual journey? Explore our curated pilgrimage packages and travel guides.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/pilgrimage"
              className="bg-white text-royal-700 hover:bg-divine-100 px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              View Pilgrimage Routes
            </Link>
            <Link
              to="/contact"
              className="bg-transparent hover:bg-white/10 text-white border-2 border-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors"
            >
              Contact for Custom Tours
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
